import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';

export const CATEGORY_COLORS = [
  { id: 'blue', name: 'Blue' },
  { id: 'green', name: 'Green' },
  { id: 'orange', name: 'Orange' },
  { id: 'red', name: 'Red' },
  { id: 'yellow', name: 'Yellow' },
];

export type CriteriaCategory = {
  /** Persisted category id. Absent for categories that only exist locally (not yet saved). */
  id?: string;
  _tempId: string;
  name: string;
  color: string;
  order: number;
};

type Props = {
  show: boolean;
  onCancel: () => void;
  onSubmit: (category: Pick<CriteriaCategory, 'name' | 'color'>) => void;
  editingCategory?: CriteriaCategory | null;
  existingNames?: string[];
};

export default function NewCategoryModal({ show, onCancel, onSubmit, editingCategory, existingNames = [] }: Props) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<{ id: string; name: string } | null>(null);

  const isEditing = !!editingCategory;

  useEffect(() => {
    if (!show) return;
    setName(editingCategory?.name ?? '');
    setColor(editingCategory ? (CATEGORY_COLORS.find((c) => c.id === editingCategory.color) ?? null) : null);
  }, [show, editingCategory]);

  const trimmedName = name.trim();
  const isDuplicateName =
    trimmedName.length > 0 &&
    existingNames.some(
      (n) => n.toLowerCase() === trimmedName.toLowerCase() && n.toLowerCase() !== editingCategory?.name?.toLowerCase()
    );

  const handleSubmit = () => {
    if (!trimmedName || !color || isDuplicateName) return;
    onSubmit({ name: trimmedName, color: color.id });
    setName('');
    setColor(null);
  };

  const handleCancel = () => {
    setName('');
    setColor(null);
    onCancel();
  };

  if (!show) return null;

  return (
    <StandardModal title={isEditing ? 'Edit Category' : 'New Category'} onCancel={handleCancel}>
      <FormContainer>
        <InputGroup
          title='Category Name'
          error={isDuplicateName ? 'A category with this name already exists' : undefined}>
          <Input
            type='text'
            placeholder='Category Name'
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </InputGroup>

        <InputGroup title='Display Color'>
          <Select
            options={CATEGORY_COLORS}
            value={color}
            onChange={(v) => setColor(v)}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.name}
            placeholder='Color'
            dataTestId='category-modal-color'
          />
        </InputGroup>

        <StyledButtonGroup>
          <Button variant='ghost' onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!trimmedName || !color || isDuplicateName}>
            Submit
          </Button>
        </StyledButtonGroup>
      </FormContainer>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  justify-content: flex-end;
  margin-top: 8px;
`;
